<template lang="pug">
  .zeus-header.main-container-wide
    Modal(v-if="modal === 'duplicate'" @close="modal = null" :header="__('Duplicate {name}', project)")
      RogueInput(label="New Gym Name")
      .buttons
        RogueButton(@click="modal = null") {{__('Cancel')}}
        RogueButton(color="black" @click="cloneProject()") {{__('Copy Project', project)}}
    Modal(v-if="modal === 'delete'" @close="modal = null" :header="__('Delete {name}', project)")
      Message(status="error")
        Icon.left-icon(name="alert-error")
        | {{__('Are you sure you want to delete this project?')}}
      .buttons
        RogueButton(@click="modal = null") {{__('Cancel')}}
        RogueButton(color="red" @click="deleteProject()") {{__('Delete Project')}}
    Modal(v-if="modal === 'rename'" @close="modal = null" :header="__('Rename Project')")
      p {{__('Current name: {name}', project)}}
      RogueInput(label="New Project Name" v-model="newName")
      .buttons
        RogueButton(@click="modal = null") {{__('Cancel')}}
        RogueButton(color="black" @click="renameProject()" :disabled="!newName") {{__('Rename')}}
    Modal(v-if="modal === 'clone'" @close="modal = null" :header="__('Clone Project')")
      RogueInput(label="New Project Name" v-model="newName")
      .buttons
        RogueButton(@click="modal = null") {{__('Cancel')}}
        RogueButton(color="black" @click="cloneProject()" :disabled="!newName") {{__('Clone Project')}}
    .slot-left
      slot(name="left")
    .slot-center
      rogue-link(to="/zeus-builder")
        StaticImage.zeus-logo-desktop(src="/UI/logos/zeus-gb-logo-black_qka63u.svg" :width="271" :height="58" alt="Zeus Gym Builder")
        StaticImage.zeus-logo-mobile(src="/UI/logos/zeus_mobile_cc3ecj.svg" :width="149" :height="38" alt="Zeus Gym Builder")
      slot(name="center")
    .slot-right
      slot(name="right")
      Tooltip(placement="bottom" :manual="true" ref="tip" theme="custom" v-if="!hideMenu")
        RogueButton( size="m" :rightIcon="tip && tip.popper ? 'arrow-up' : 'arrow-down'" @click="tip.toggle()" ) {{ __('Menu', 'zeus') }}
        template(#tooltip)
          .dropdown
            .menu-dropdown
              rogue-link(to="/zeus-builder" v-if="!onDashboard") {{ loggedIn ? __('My ZEUS Dashboard', 'zeus') : __('ZEUS Dashboard') }}
              rogue-link.not-mobile( v-if="!project.isNew" to="/zeus-builder/project/edit?templateId=new") {{ __('Create a New Gym', 'zeus') }}
              rogue-link.not-mobile( v-if="project.canEdit" :to="'/zeus-builder/project/edit?projectId='+project._id") {{__('Edit Project')}}
              span.link( v-if="project.canEdit" @click="modal = 'rename'" ) {{__('Rename Project')}}
              span.link.not-mobile( v-if="project.canEdit" @click="modal = 'duplicate'" ) {{__('Copy Project')}}
              span.link( v-if="project.canEdit" @click="modal = 'delete'") {{__('Delete Project')}}
              rogue-link(to="/contact-us") {{ __('Contact Us', 'zeus') }}
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useRogue } from '@/js/services/Rogue'
import type { GymProject } from '../types'

const props = withDefaults(defineProps<{
  project?: Partial<GymProject>
  hideMenu?: boolean
}>(), {
  project  : () => ({}),
  hideMenu : false,
})

const customerStore = useCustomerStore()
const route = useRoute()
const router = useRouter()
const rogue = useRogue()

// Tooltip is registered globally, so there is no imported component type to bind the ref to.
const tip = ref<any>(null)

const modal = ref<'duplicate' | 'delete' | 'rename' | 'clone' | null>(null)
const newName = ref('')

const loggedIn = computed(() => customerStore.loggedIn)
const onDashboard = computed(() => route.path === '/zeus-builder')

async function deleteProject() {
  await rogue.fetch(`/svc_gym/projects/${props.project._id}`, 'delete')
  modal.value = null
  router.push('/zeus-builder/my-gyms')
}

async function renameProject() {
  await rogue.fetch(`/svc_gym/projects/${props.project._id}`, 'patch', { name: newName.value })
  props.project.name = newName.value
  modal.value = null
}

async function cloneProject() {
  const data = await rogue.fetch<GymProject>('/svc_gym/projects', 'post', {
    name       : newName.value,
    templateId : props.project._id,
  })
  router.push(`/zeus-builder/project/${data._id}`)
  modal.value = null
}
</script>

<style lang="scss" scoped>
.zeus-header {
  display: flex;
  justify-content: space-between;
  padding: $space-xs 0;
  align-items: center;
}

.rg-img {
  width: 215px;
}

.buttons {
  display: flex;
  margin-top: $space-s;
  justify-content: center;

  .button:last-child {
    margin-left: $space-s;
  }
}

.slot-left, .slot-right {
  flex-basis: 50%;
}

@include mobile(){
  .slot-left:empty {
    display: none;
  }
}

.slot-right {
  justify-content: flex-end;
}

.zeus-logo-desktop {
  display: none;
}

.zeus-logo-mobile {
  height: 38px;
  width: 149px;
}

@include not-mobile(){
  .zeus-logo-desktop {
    display: block;
  }
  .zeus-logo-mobile {
    display: none;
  }
}

.menu-dropdown {
  a, .link {
    display: block;
    color: $color-black;
    font-size: $size-xs;
    line-height: 44px;
    padding: 0 $size-xs;
    border-top: 1px solid $color-gray-light;
    text-decoration: none;
    white-space: nowrap;

    @include mobile() {
      &.not-mobile {
        display: none;
      }
    }

    &:hover {
      background-color: $color-gray-x-light;
    }
  }
}

.slot-right, .slot-left, .slot-middle {
  display: flex;

  > *:not(:last-child) {
    margin-right: $space-s;
  }
}

</style>
